import {Box} from "@mui/material";
import Typography from "@mui/material/Typography";
import type {TFunction} from "i18next";
import React, {useId, useRef} from "react";
import {flushSync} from "react-dom";
import {SigningFormat} from "./format.util";
import {SnapshotTip} from "./digitalSigning.types";
import TipCustomAmount from "./TipCustomAmount";
import TipPill from "./TipPill";
import {TipChoice, tipPresetDetail, tipPresetLabel} from "./tip.util";

const phoneColumns = (count: number) => (count === 4 ? 2 : Math.min(count, 3));

interface Props {
    tip: SnapshotTip;
    choice: TipChoice;
    customInput: string;
    onChange?: (choice: TipChoice, customInput: string) => void;
    format: SigningFormat;
    language: string;
    t: TFunction;
    disabled?: boolean;
    readOnly?: {customCents?: number};
}

export const tipOffered = (tip: SnapshotTip | undefined): tip is SnapshotTip => (
    !!tip && (!!tip.options?.length || tip.customAllowed)
);

function TipSelector({tip, choice, customInput, onChange, format, language, t, disabled, readOnly}: Props) {
    const headingId = useId();
    const descriptionId = useId();
    const heading = tip.labels?.[language]?.trim() || t('Tip');
    const description = tip.descriptions?.[language]?.trim();
    const options = tip.options ?? [];
    const customOffered = tip.customAllowed;
    const showBase = tip.appliedFormat === 'PERCENT' && tip.base === 'NET_BEFORE_TAX' && options.length > 0 && tip.baseCents > 0;
    const percentOffered = options.some(option => !!tipPresetDetail(option, tip));
    const chosen = choice.mode === 'PRESET' ? options.find(option => option.index === choice.presetIndex) : undefined;
    const detail = chosen && tipPresetDetail(chosen, tip);
    const pick = (next: TipChoice) => onChange?.(next, customInput);

    const customInputRef = useRef<HTMLInputElement>(null);
    const pickCustom = () => {
        flushSync(() => pick({mode: 'CUSTOM'}));
        customInputRef.current?.focus();
    };

    const count = 1 + options.length + (customOffered ? 1 : 0);
    const common = {disabled, readOnly: !!readOnly};

    return (
        <Box role={'group'} aria-labelledby={headingId} aria-describedby={description ? descriptionId : undefined}>
            <Typography id={headingId} variant={'subtitle1'} component={'h2'}>
                {heading}
                <Box component={'span'} sx={{color: 'text.secondary', fontWeight: 400}}>
                    {' · '}{t('optional')}
                </Box>
            </Typography>
            {!!description && (
                <Typography id={descriptionId} variant={'body1'} color={'text.secondary'} sx={{mt: 0.5, textWrap: 'pretty'}}>
                    {description}
                </Typography>
            )}

            <Box
                component={readOnly ? 'ul' : 'div'}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: `repeat(${phoneColumns(count)}, minmax(0, 1fr))`, sm: `repeat(${count}, minmax(0, 1fr))`},
                    gap: 1,
                    mt: 1.5,
                    mb: 0,
                    p: 0,
                    listStyle: 'none',
                }}
            >
                <TipPill
                    {...common}
                    selected={choice.mode === 'NONE'}
                    onClick={() => pick({mode: 'NONE'})}
                    label={t('No tip', {context: 'pill'})}
                />

                {options.map(option => (
                    <TipPill
                        {...common}
                        key={option.index}
                        selected={choice.mode === 'PRESET' && choice.presetIndex === option.index}
                        highlighted={tip.highlightedPresetIndex === option.index}
                        onClick={() => pick({mode: 'PRESET', presetIndex: option.index})}
                        label={tipPresetLabel(option, tip.appliedFormat, format)}
                    />
                ))}

                {customOffered && (
                    <TipPill
                        {...common}
                        selected={choice.mode === 'CUSTOM'}
                        onClick={pickCustom}
                        label={t('Other', {context: 'pill'})}
                    />
                )}
            </Box>

            {customOffered && choice.mode === 'CUSTOM' ? (
                <TipCustomAmount
                    inputRef={customInputRef}
                    tip={tip}
                    value={customInput}
                    onChange={value => onChange?.({mode: 'CUSTOM'}, value)}
                    format={format}
                    t={t}
                    disabled={disabled}
                    readOnlyCents={readOnly ? readOnly.customCents ?? 0 : undefined}
                />
            ) : percentOffered && (
                <Typography
                    variant={'body2'}
                    color={'text.secondary'}
                    aria-live={readOnly ? undefined : 'polite'}
                    sx={{mt: 1, minHeight: '1.45em'}}
                >
                    {!!detail && t(detail.rounded ? '{{amount}} · {{percent}} of {{base}}, rounded' : '{{amount}} · {{percent}} of {{base}}', {
                        amount: format.money(detail.amountCents),
                        percent: format.percent(detail.percent),
                        base: format.money(detail.baseCents),
                    })}
                </Typography>
            )}

            {showBase && (
                <Typography variant={'body2'} color={'text.secondary'} sx={{mt: 1.5}}>
                    {t('Based on {{amount}} before tax', {amount: format.money(tip.baseCents)})}
                </Typography>
            )}
        </Box>
    );
}

export default TipSelector;
